import { useCallback, useEffect, useState } from "react";
import { getMode } from "@/lib/mode";
import { trackEvent } from "@/lib/audit";

const ENABLED_KEY = "multi_match_enabled";
const EXPANDED_KEY = "multi_match_expanded";
const FOLLOWED_KEY = "followed_matches";
const MUTED_KEY = "multi_match_muted";

function readBool(k: string, def: boolean) {
  if (typeof window === "undefined") return def;
  const v = localStorage.getItem(k);
  if (v === null) return def;
  return v === "1" || v === "true";
}

export function useMultiMatch() {
  const [enabled, setEnabled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [followed, setFollowed] = useState<string[]>([]);
  const [muted, setMuted] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const defaultEnabled = getMode() === "betting";
    setEnabled(readBool(ENABLED_KEY, defaultEnabled));
    setExpanded(readBool(EXPANDED_KEY, false));
    setMuted(readBool(MUTED_KEY, false));
    try {
      const raw = localStorage.getItem(FOLLOWED_KEY);
      setFollowed(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      setFollowed([]);
    }
    setHydrated(true);
  }, []);

  const toggleEnabled = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem(ENABLED_KEY, next ? "1" : "0"); } catch { /* noop */ }
      void trackEvent(next ? "multi_match_enabled" : "multi_match_disabled", {});
      return next;
    });
  }, []);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      try { localStorage.setItem(EXPANDED_KEY, next ? "1" : "0"); } catch { /* noop */ }
      void trackEvent(next ? "multi_match_expanded" : "multi_match_collapsed", {});
      return next;
    });
  }, []);

  const toggleFollow = useCallback((id: string) => {
    setFollowed((prev) => {
      const isFollowed = prev.includes(id);
      const next = isFollowed ? prev.filter((x) => x !== id) : [...prev, id];
      try { localStorage.setItem(FOLLOWED_KEY, JSON.stringify(next)); } catch { /* noop */ }
      void trackEvent(isFollowed ? "match_unfollowed" : "match_followed", { matchId: id });
      return next;
    });
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem(MUTED_KEY, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  }, []);

  return {
    enabled,
    expanded,
    followed,
    muted,
    hydrated,
    toggleEnabled,
    toggleExpanded,
    toggleFollow,
    toggleMuted,
  };
}
